import { autoinject, transient } from "aurelia-dependency-injection";
import { BindingEngine } from "aurelia-binding";
import { Router } from "aurelia-router";
import { I18N } from 'aurelia-i18n';
import { UnitOfWorkObserver } from "../../../../utilities/unit-of-work-observer";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { ValidationRule, ValidationType, EntityNavigationMapper, OneLevelNestedProperty, TwoLevelNestedProperty } from "../../../../models/validations/validations.models";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { EntityTypeRelation } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { EnumUtilities } from "../../../../utilities/enum-utilities";
import { TableMetaData } from "../../../../models/lookups/table-metadata";
import { TableColumnMetaData } from "../../../../models/lookups/table-column-metadata";
import * as Enumerable from 'linq';
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { BindingSignaler } from "aurelia-templating-resources";
import { Reflection } from "../../../../utilities/reflection";
import { PermissionEntity } from "../../../../models/manage/permissions.models";
import { BaseClaims } from "../../../../constants/authentication/base-claims";

@autoinject
@transient()
export class Validations {

    public typeId: number;
    public id: number;
    public entityName: string;
    public entityTypeDisplayName: string;
    public validationsUow: UnitOfWorkObserver;
    public validations: ValidationRule[] = [];
    public entityNavigationMappers: EntityNavigationMapper[];
    public primitivePropertyValidationsPerType: { [prop: number]: ValidationRule[]; } = {};
    public collectionPropertyValidationsPerType: { [prop: number]: ValidationRule[]; } = {};
    public twoLevelPropertyValidationsPerType: { [prop: number]: ValidationRule[]; } = {};
    public collectionProperties: { [prop: number]: EntityTypeRelation[]; } = {};
    public oneLevelValidationsPerType: { [prop: number]: ValidationRule[]; } = {};
    public canUpdateValidations: boolean = true;
    public canAddValidations: boolean = true;
    public canDeleteValidations: boolean = true;
    public validationTypes: ValidationType[] = [];
    public tablesMetadata: TableMetaData[];
    public tablesColumnsMetadata: TableColumnMetaData[];
    public selectedEntityNavigationMapper: EntityNavigationMapper = null;
    public loading: boolean = true;
    public entityTypeRelations: EntityTypeRelation[];
    public usePassedCancelOperation?: boolean;
    public showCloseButton: boolean = false;
    //used to operate cancel operation from another class(like in manage users)
    private _cancelOperation?: () => void = null;

    constructor(private validationsClientService: ValidationsClientService,
        private engine: BindingEngine,
        private notificationClientService: NotificationClientService,
        private router: Router,
        private bindingSignaler: BindingSignaler,
        private userClaimsService: UserClaimsService,
        private i18N: I18N) {
        this.canUpdateValidations = this.userClaimsService.hasPermission(BaseClaims.CanUpdatePermissions);
    }

    public activate(params): void {
        params.type = Number(params.type);
        this.typeId = params.type;
        this.id = params.id;
        this.entityName = params.name;
        this.validations = [];
        this.entityNavigationMappers = [];
        this.validationTypes = [];
        this.tablesMetadata = [];
        this.entityTypeRelations = [];
        this.tablesColumnsMetadata = [];
        this.twoLevelPropertyValidationsPerType = {};
        this.primitivePropertyValidationsPerType = {};
        this.collectionPropertyValidationsPerType = {};
        this.oneLevelValidationsPerType = {};
        this.collectionProperties = {};
        this.selectedEntityNavigationMapper = null;

        this.usePassedCancelOperation = params.usePassedCancelOperation != null ? params.usePassedCancelOperation : this.usePassedCancelOperation = false;
        this.showCloseButton = params.showCloseButton != null ? params.showCloseButton : false;
        this._cancelOperation = params.cancelOperation != null ? params.cancelOperation : null;
        if (this.typeId === PermissionEntity.Branch) {
            this.entityTypeDisplayName = this.i18N.tr('branch-text');
        } else if (this.typeId === PermissionEntity.Department) {
            this.entityTypeDisplayName = this.i18N.tr('department-text');
        } else if (this.typeId === PermissionEntity.Role) {
            this.entityTypeDisplayName = this.i18N.tr('role-text');
        } else if (this.typeId === PermissionEntity.User) {
            this.entityTypeDisplayName = this.i18N.tr('user-text');
        }
        this.getAllValidations();
    }

    public getAllValidations(): void {
        if (this.validationsUow) {
            this.validationsUow.dispose();
        }

        setTimeout(() => {
            this.validationsClientService.getValidationRulesForLevel({ validationEntity: this.typeId, entityId: this.id }).then(x => {
                this.validations = x.validationRules;
                this.entityNavigationMappers = x.entityTrees;
                this.validationTypes = x.validationTypes;
                this.tablesMetadata = x.tablesMetadata;
                this.entityTypeRelations = x.entityTypeRelations;
                this.tablesColumnsMetadata = Enumerable.from(x.tablesMetadata).selectMany(x => x.tableColumnMetadatas).where(x => !x.isPrimaryKeyColumn).toArray();
                this.entityNavigationMappers.forEach(mapper => {
                    mapper.oneLevelNestedProperties =
                        mapper.oneLevelNestedProperties.filter(x => this.tablesColumnsMetadata.filter(y => y.tableMetadataId == x.tableMetadataId).length > 0);

                    if (!this.primitivePropertyValidationsPerType[mapper.rootEntityType]) {
                        this.primitivePropertyValidationsPerType[mapper.rootEntityType] = [];
                    }
                    if (!this.oneLevelValidationsPerType[mapper.rootEntityType]) {
                        this.oneLevelValidationsPerType[mapper.rootEntityType] = [];
                    }
                    if (!this.twoLevelPropertyValidationsPerType[mapper.rootEntityType]) {
                        this.twoLevelPropertyValidationsPerType[mapper.rootEntityType] = [];
                    }
                    if (!this.collectionPropertyValidationsPerType[mapper.rootEntityType]) {
                        this.collectionPropertyValidationsPerType[mapper.rootEntityType] = [];
                    }
                });
                this.validations.forEach(validation => {

                    if (!validation.childEntityType && this.primitivePropertyValidationsPerType[validation.rootEntityType] && !validation.isCollectionTypeValidationRule) {
                        this.primitivePropertyValidationsPerType[validation.rootEntityType].push(validation);
                    } else if (validation.childEntityType && validation.tableColumnMetadataId && !validation.isCollectionTypeValidationRule) {
                        const entityMapper = this.entityNavigationMappers.filter(x => x.rootEntityType == validation.rootEntityType)[0];
                        if (this.oneLevelValidationsPerType[validation.rootEntityType] && entityMapper.oneLevelNestedProperties.some(z => z.childEntityType == validation.childEntityType && this.tablesMetadata.some(y => y.id == z.tableMetadataId && y.tableColumnMetadatas.some(d => d.id == validation.tableColumnMetadataId))))
                            this.oneLevelValidationsPerType[validation.rootEntityType].push(validation);
                        else if (this.twoLevelPropertyValidationsPerType[validation.rootEntityType]) {
                            this.twoLevelPropertyValidationsPerType[validation.rootEntityType].push(validation);
                        }
                    }
                    if (validation.isCollectionTypeValidationRule) {
                        this.collectionPropertyValidationsPerType[validation.rootEntityType].push(validation);
                    }
                });
                this.entityTypeRelations.forEach(x => {
                    if (!this.collectionProperties[x.parentEntityType]) {
                        this.collectionProperties[x.parentEntityType] = [];
                    }
                    this.collectionProperties[x.parentEntityType].push(x);
                });
                this.selectedEntityNavigationMapper = this.entityNavigationMappers[0];
                this.loading = false;
                setTimeout(() => {
                    this.validationsUow = UnitOfWorkObserver.getTrackingInstance(this.engine, { validations: this.validations }, []);
                });
            },  () => { });
        });
    }

    getOneLevelValidations(prop: OneLevelNestedProperty) {
        return this.oneLevelValidationsPerType[this.selectedEntityNavigationMapper.rootEntityType].filter(x => x.childEntityType == prop.childEntityType);
    }

    getTwoLevelValidations(prop: TwoLevelNestedProperty) {
        const oneLevelProperties = prop.oneLevelNestedProperties;
        const tableColumnMetadataIds = Enumerable.from(this.tablesMetadata.filter(x => oneLevelProperties.some(y => y.tableMetadataId == x.id))).selectMany(x => x.tableColumnMetadatas).toArray();
        return this.twoLevelPropertyValidationsPerType[this.selectedEntityNavigationMapper.rootEntityType].filter(x => x.childEntityType == prop.childEntityType &&
            tableColumnMetadataIds.some(d => d.id == x.tableColumnMetadataId));
    }

    public getEntityName(entityType: number) {
        return entityType ? EnumUtilities.getName(EntityTypes, entityType) : null;
    }

    public getValidationTypes(entityType: number, tableColumnMetadataId: number) {
        const tableColumnMetadata = this.tablesColumnsMetadata.filter(x => x.id == tableColumnMetadataId)[0];
        const response = this.validationTypes && tableColumnMetadata
            ? this.validationTypes.filter(x => (!x.supportedEntities || x.supportedEntities.length == 0 || x.supportedEntities.some(y => y == entityType))
                && (!x.supportedPropertyTypes || x.supportedPropertyTypes.length == 0 || x.supportedPropertyTypes.some(y => y == tableColumnMetadata.tableColumnDataTypeId)))
            : this.validationTypes;
        return response;
    }

    public goBack(): void {
        if (this.usePassedCancelOperation) {
            this._cancelOperation();
        }
        else {
            this.router.navigateBack();
        }
    }

    public areThereUnsavedChanges() {
        if (this.validationsUow) {
            return this.validationsUow.getHasChanges;
        }
        else {
            return false;
        }
    }

    public getSelectedOneLevelProperties(validationRule: ValidationRule) {
        const res = this.selectedEntityNavigationMapper.twoLevelNestedProperties.filter(x => x.childEntityType == validationRule.childEntityType)[0];
        return res ? res.oneLevelNestedProperties : null;
    }

    getSelected(validationRule: ValidationRule) {
        if (this.selectedEntityNavigationMapper.twoLevelNestedProperties) {
            const prop = this.selectedEntityNavigationMapper.twoLevelNestedProperties.filter(x => x.childEntityType == validationRule.childEntityType)[0];
            if (!prop) {
                return null;
            }
            const oneLevelProperties = prop.oneLevelNestedProperties;
            const selectedTableColumnMetadata = this.tablesColumnsMetadata.filter(x => validationRule.tableColumnMetadataId == x.id)[0].tableMetadataId;
            const selectedTable = this.tablesMetadata.filter(x => x.id == selectedTableColumnMetadata)[0];
            return oneLevelProperties.filter(x => x.tableMetadataId == selectedTable.id)[0];
        }
        return null;
    }

    containsColumns(tableMetadatId: number) {
        return this.tablesColumnsMetadata && this.tablesColumnsMetadata.filter(x => tableMetadatId == x.tableMetadataId).length > 0;
    }

    getTableColumns(entityNavigationMapper: EntityNavigationMapper, validationRule: ValidationRule, level: number) {
        if (level == 0) {
            const tableMetadata = this.tablesMetadata.filter(x => x.id == entityNavigationMapper.tableMetadataId)[0];
            let arr = entityNavigationMapper && entityNavigationMapper.tableMetadataId && tableMetadata ? tableMetadata.tableColumnMetadatas : null;
            arr = arr ? arr.filter(c => !c.isPrimaryKeyColumn) : arr;
            return arr && arr.length > 0 ? arr : null;
        } else if (level == 1) {
            const entity = entityNavigationMapper.oneLevelNestedProperties.filter(x => x.childEntityType == validationRule.childEntityType)[0];
            const arr = entity ? this.tablesColumnsMetadata.filter(x => entity.tableMetadataId == x.tableMetadataId) : null;
            return arr && arr.length > 0 ? arr : null;
        } else if (level == 2) {
            const entity = entityNavigationMapper.twoLevelNestedProperties.filter(x => x.childEntityType == validationRule.childEntityType)[0];
            if (entity == null) {
                return null;
            }
            const tableMetadata = this.tablesColumnsMetadata.filter(x => x.id == validationRule.tableColumnMetadataId)[0];
            if (tableMetadata) {
                const selectedNestedProperty = entity.oneLevelNestedProperties.filter(x => x.tableMetadataId == tableMetadata.tableMetadataId)[0];
                return entity && selectedNestedProperty ? this.tablesColumnsMetadata.filter(x => selectedNestedProperty.tableMetadataId == x.tableMetadataId) : null;
            }
            return null;
        }
        return null;
    }

    public addNewPropertyValidation(collection: { [prop: number]: ValidationRule[]; }, prop: OneLevelNestedProperty | TwoLevelNestedProperty) {
        const newValidationRule = {
            childEntityType: prop && prop.childEntityType ? prop.childEntityType : null,
            dbId: 0,
            entityTypeRelation: null,
            entityTypeRelationId: null,
            isCollectionTypeValidationRule: false,
            rootEntityType: this.selectedEntityNavigationMapper.rootEntityType,
            tableColumnMetadata: null,
            tableColumnMetadataId: null,
            validationParam: null,
            validationMessage: null,
            validationType: null
        };
        collection[this.selectedEntityNavigationMapper.rootEntityType].push(newValidationRule);
        this.validations.push(newValidationRule);
        if (prop && 'oneLevelNestedProperties' in prop) {
            const tableColumnMetadataIds = Enumerable.from(this.tablesMetadata.filter(x => (<TwoLevelNestedProperty>prop).oneLevelNestedProperties.some(y => y.tableMetadataId == x.id))).selectMany(x => x.tableColumnMetadatas).toArray();
            newValidationRule.tableColumnMetadataId = tableColumnMetadataIds[0].id;
            this.bindingSignaler.signal('two-level-validations-' + prop.childEntityType);
        } else if (prop) {
            this.bindingSignaler.signal('validations-' + prop.childEntityType);
        }
    }

    onOneLevelValidationRefresh(validationRule: ValidationRule) {
        this.bindingSignaler.signal('validation-' + validationRule.dbId);
    }

    onTwoLevelValidationRefresh(validationRule: ValidationRule) {
        this.bindingSignaler.signal('two-level-validation-' + validationRule.dbId);
    }

    onSelectedNestedPropertyChanged($event, validationRule: ValidationRule) {
        const item = $event.detail.item;
        if (item && item.tableMetadataId > 0) {
            const tableMetadatas = this.tablesMetadata.filter(x => item.tableMetadataId == x.id)[0].tableColumnMetadatas;
            validationRule.tableColumnMetadataId = tableMetadatas.some(x => x.id == validationRule.tableColumnMetadataId) ? validationRule.tableColumnMetadataId : tableMetadatas[0].id;
            setTimeout(() => {
                this.bindingSignaler.signal('two-level-validation-nested-prop-' + validationRule.dbId);
            });
        }
    }

    public removeValidation(validationRule: ValidationRule, collection: { [prop: number]: ValidationRule[]; }, prop: OneLevelNestedProperty | TwoLevelNestedProperty) {
        if (collection) {
            const index = collection[this.selectedEntityNavigationMapper.rootEntityType].indexOf(validationRule);
            if (index > -1) {
                collection[this.selectedEntityNavigationMapper.rootEntityType].splice(index, 1);
            }
        }
        if (this.validations) {
            const index = this.validations.indexOf(validationRule);
            if (index > -1) {
                this.validations.splice(index, 1);
            }
        }
        if (prop && Reflection.getPropertyName<TwoLevelNestedProperty>(x => x.oneLevelNestedProperties) in prop) {
            this.bindingSignaler.signal('two-level-validations-' + prop.childEntityType);
        } else if (prop) {
            this.bindingSignaler.signal('validations-' + prop.childEntityType);
        }
    }

    public save(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.validationsClientService.save({ changes: this.validationsUow.getChanges(), validationEntity: this.typeId, entityId: this.id }).then(() => {
                this.notificationClientService.show(this.i18N.tr('validations-saved-successfully-text'), "", NotificationType.Success);
                setTimeout(() => {
                    this.validationsUow.dispose();
                    this.validationsUow = UnitOfWorkObserver.getTrackingInstance(this.engine, { validations: this.validations }, []);
                });
                resolve();
            }).catch(() => {
                this.notificationClientService.show(this.i18N.tr('failed-to-persist-validation-changes-text'), "", NotificationType.Error);
                reject();
            });
        });
    }
}
